/**
 * Small synthesized sound effects (no audio files). Sounds are generated with the Web Audio API, play
 * at a low volume, and can be switched off with the sound toggle in the game. The choice is remembered.
 */

export type SoundName =
  | "alarm"
  | "yourTurn"
  | "fascistPolicy"
  | "liberalPolicy"
  | "victory"
  | "defeat"
  | "heartbeat" // the execution countdown: a low double thump, higher and sharper each time (pass the step 0-2)
  | "revealHitler"
  | "revealSafe"
  | "drumroll" // the pause before an award winner is named
  | "awardReveal"; // the winner is named

const STORAGE_KEY = "soundEnabled";
const MASTER_VOLUME = 0.06;

let audioContext: AudioContext | undefined;
let enabled = readPreference();

function readPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "false";
  } catch (e) {
    return true; // Storage can be unavailable (private windows, blocked site data).
  }
}

export function isSoundEnabled(): boolean {
  return enabled;
}

export function setSoundEnabled(on: boolean): void {
  enabled = on;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(on));
  } catch (e) {
    // Not being able to remember the choice is fine.
  }
}

function getContext(): AudioContext | undefined {
  try {
    if (audioContext === undefined) {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext || (window as any).webkitAudioContext;
      if (Ctor === undefined) {
        return undefined;
      }
      audioContext = new Ctor();
    }
    if (audioContext.state === "suspended") {
      audioContext.resume();
    }
    return audioContext;
  } catch (e) {
    return undefined;
  }
}

/** Plays one note with a quick attack and an exponential fade. */
function note(
  ctx: AudioContext,
  frequency: number,
  startOffset: number,
  duration: number,
  type: OscillatorType,
  volume: number = 1,
  endFrequency?: number
): void {
  const start = ctx.currentTime + startOffset;
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  if (endFrequency !== undefined) {
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
  }

  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(MASTER_VOLUME * volume, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.05);
}

/** Plays a sound effect, if sound is enabled and the browser allows audio. Never throws. */
export function playSound(name: SoundName, step: number = 0): void {
  if (!enabled) {
    return;
  }
  const ctx = getContext();
  if (ctx === undefined) {
    return;
  }

  try {
    switch (name) {
      case "alarm": // Hitler zone: two alternating warning tones, three times.
        for (let i = 0; i < 3; i++) {
          note(ctx, 440, i * 0.36, 0.16, "square", 0.8);
          note(ctx, 554, i * 0.36 + 0.18, 0.16, "square", 0.8);
        }
        break;
      case "yourTurn": // A friendly two-note ding.
        note(ctx, 660, 0, 0.18, "sine", 1.4);
        note(ctx, 880, 0.14, 0.3, "sine", 1.4);
        break;
      case "fascistPolicy": // A low, heavy thud.
        note(ctx, 140, 0, 0.45, "sawtooth", 1.2, 55);
        break;
      case "liberalPolicy": // A bright rising chime.
        note(ctx, 523, 0, 0.3, "sine", 1.3);
        note(ctx, 659, 0.1, 0.3, "sine", 1.3);
        note(ctx, 784, 0.2, 0.45, "sine", 1.3);
        break;
      case "victory":
        [523, 659, 784, 1047].forEach((f, i) => note(ctx, f, i * 0.14, 0.4, "triangle", 1.5));
        break;
      case "defeat":
        [392, 330, 262].forEach((f, i) => note(ctx, f, i * 0.2, 0.5, "triangle", 1.3));
        break;
      case "heartbeat": {
        // Lub-dub, getting louder and faster as the count runs down.
        const gap = 0.24 - step * 0.03;
        const level = 3 + step * 0.9;
        note(ctx, 90 + step * 12, 0, 0.2, "sine", level, 42);
        note(ctx, 380, 0, 0.04, "square", 0.25);
        note(ctx, 80 + step * 12, gap, 0.26, "sine", level * 0.85, 38);
        break;
      }
      case "revealHitler": // A dark swell, then a heavy hit.
        [196, 233, 277].forEach((f) => note(ctx, f, 0, 0.9, "sawtooth", 0.7));
        note(ctx, 110, 0.05, 0.9, "sawtooth", 0.8);
        note(ctx, 60, 0.7, 0.9, "sine", 4, 30);
        break;
      case "revealSafe": // A hollow sigh: tension let out, but nothing won.
        note(ctx, 349, 0, 0.5, "triangle", 1.3);
        note(ctx, 262, 0.28, 0.8, "triangle", 1.3);
        break;
      case "drumroll": // A quick roll that swells.
        for (let i = 0; i < 14; i++) {
          note(ctx, 150, i * 0.07, 0.06, "triangle", 0.8 + i * 0.12, 120);
        }
        break;
      case "awardReveal": // A bright two-note sting.
        note(ctx, 523, 0, 0.25, "triangle", 1.4);
        note(ctx, 784, 0.12, 0.6, "triangle", 1.6);
        break;
      default:
    }
  } catch (e) {
    // Audio is a nicety; never let it break the game.
  }
}
